import { useEffect } from 'react';

/* ─────────────────────────────────────────────
   KFX JOURNAL — ABOUT
───────────────────────────────────────────── */

const SUPPORT_EMAIL = 'info@kfxjournal.com';
const FOUNDER_EMAIL = 'honofx@gmail.com';
const COMPANY_NAME = 'KFX Journal';

const sections = [
  {
    id: 'what-is-kfx',
    title: '1. What is KFX Journal?',
    content: [
      {
        type: 'text',
        text: `${COMPANY_NAME} is a forex and futures trading journal and performance analytics platform built for East African traders. It combines automated trade logging, analytics dashboards, AI-powered coaching, and a What-If trade simulator to help traders track their edge and build consistency.`,
      },
      {
        type: 'text',
        text: 'The platform lets traders log trades across forex pairs, metals, and indices, review win rate and rule-adherence analytics, and get AI-generated feedback on patterns in their own trading history.',
      },
    ],
  },
  {
    id: 'who-founded',
    title: '2. Who founded KFX Journal?',
    content: [
      {
        type: 'text',
        text: 'KFX Journal was founded by MUGWANEZA Prince Honore, known online as Prino or Honofx, a NASDAQ futures trader (NQ / NAS100 / USTECH, alongside ES) and self-taught full-stack developer from Rubavu, Rwanda, now based in Kigali.',
      },
      {
        type: 'text',
        text: 'Prino has traded forex and futures since 2022, using an ICT / Smart Money Concepts approach focused on the New York AM session. He built KFX Journal to solve a gap he experienced directly as a trader: the lack of journaling tools designed around East African traders\' needs, payment methods, and market focus.',
      },
      {
        type: 'text',
        text: 'He is also the founder of Kigali Forex University, a Rwandan academy for teaching and mentoring traders in forex and futures markets.',
      },
    ],
  },
  {
    id: 'who-for',
    title: '3. Who is KFX Journal for?',
    content: [
      {
        type: 'text',
        text: 'KFX Journal is built primarily for forex and futures traders in Rwanda and the broader East African region, though the platform is open to any retail trader who wants structured trade tracking and AI-assisted review of their performance.',
      },
    ],
  },
  {
    id: 'what-you-can-do',
    title: '4. What can you do with KFX Journal?',
    content: [
      {
        type: 'list',
        items: [
          'Log trades with detailed entry, exit, and reasoning fields',
          'Track performance analytics across forex pairs, metals (XAUUSD, XAGUSD), and indices (US100/NASDAQ)',
          'Get AI Coach reports that surface patterns and milestones in your trading',
          'Run What-If simulations to test alternate trade outcomes',
          'Keep a trading notebook alongside your trade history',
        ],
      },
      {
        type: 'highlight',
        text: 'KFX Journal is a performance tracking and coaching tool — not a financial adviser, broker, or regulated investment service. Nothing on the platform constitutes financial advice.',
      },
    ],
  },
  {
    id: 'contact',
    title: '5. Get in touch',
    content: [
      {
        type: 'text',
        text: `For support or account questions, reach us at ${SUPPORT_EMAIL}. For founder or partnership inquiries, contact ${FOUNDER_EMAIL} directly.`,
      },
    ],
  },
];

/* ─────────────────────────────────────────────
   Reused block renderer — mirrors the one in
   TermsOfService.jsx / PrivacyPolicy.jsx
───────────────────────────────────────────── */
function PolicySection({ section }) {
  return (
    <div id={section.id} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em', marginBottom: 18 }}>
        {section.title}
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {section.content.map((block, i) => {
          if (block.type === 'text') return (
            <p key={i} style={{ fontSize: 14.5, color: 'var(--text-65)', lineHeight: 1.8, fontFamily: "'Bricolage Grotesque',sans-serif" }}>{block.text}</p>
          );
          if (block.type === 'subheading') return (
            <h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-70)', marginTop: 6 }}>{block.text}</h3>
          );
          if (block.type === 'list') return (
            <ul key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 20 }}>
              {block.items.map((item, j) => (
                <li key={j} style={{ fontSize: 14.5, color: 'var(--text-60)', lineHeight: 1.7, fontFamily: "'Bricolage Grotesque',sans-serif" }}>{item}</li>
              ))}
            </ul>
          );
          if (block.type === 'highlight') return (
            <div key={i} style={{ padding: '14px 16px', borderRadius: 8, background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.18)' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(212,168,67,0.85)', lineHeight: 1.7, fontFamily: "'Bricolage Grotesque',sans-serif" }}>{block.text}</p>
            </div>
          );
          return null;
        })}
      </div>
    </div>
  );
}

export default function AboutPage({ onNavigate }) {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg, #070707)',
      color: 'var(--text, #EDE8DE)',
      fontFamily: "'Bricolage Grotesque',sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,700;12..96,800&family=DM+Mono:wght@400;500&display=swap');
        :root {
          --bg: #070707; --surface: #0f0f0d; --text: #EDE8DE;
          --text-70: rgba(237,232,222,0.70); --text-65: rgba(237,232,222,0.65);
          --text-60: rgba(237,232,222,0.60); --text-55: rgba(237,232,222,0.55);
          --text-45: rgba(237,232,222,0.45);
          --gold: #D4A843; --overlay-2: rgba(255,255,255,0.02);
          --overlay-5: rgba(255,255,255,0.05); --overlay-7: rgba(255,255,255,0.07);
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }

        @media (max-width: 700px) {
          .kfx-layout { grid-template-columns: 1fr !important; gap: 0 !important; padding: 32px 16px 72px !important; }
          .kfx-sidebar {
            position: static !important; margin-bottom: 28px; padding: 12px 14px;
            background: rgba(255,255,255,0.02); border: 1px solid rgba(212,168,67,0.1); border-radius: 10px;
          }
          .kfx-sidebar-label { margin-bottom: 10px !important; }
          .kfx-toc-nav { flex-direction: row !important; flex-wrap: wrap !important; gap: 4px !important; }
          .kfx-toc-nav a { font-size: 11px !important; padding: 4px 8px !important; background: rgba(212,168,67,0.04) !important; border-radius: 5px; line-height: 1.3 !important; }
          .kfx-sidebar-also { margin-top: 12px !important; }
          .kfx-nav { padding: 0 16px !important; }
          .kfx-nav-subtitle { display: none !important; }
          .kfx-back-btn { font-size: 10px !important; padding: 6px 12px !important; letter-spacing: 0.04em !important; }
          .kfx-page-header { margin-bottom: 32px !important; }
          .kfx-page-header h1 { font-size: 28px !important; }
        }
      `}</style>

      {/* Nav */}
      <nav className="kfx-nav" style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(7,7,7,0.92)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(212,168,67,0.1)', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>KFX Journal</div>
          <div className="kfx-nav-subtitle" style={{ fontSize: 8, color: 'rgba(212,168,67,0.5)', fontFamily: "'DM Mono',monospace", letterSpacing: '0.1em', textTransform: 'uppercase' }}>Trading Intelligence</div>
        </button>
        <button className="kfx-back-btn" onClick={() => onNavigate('home')} style={{ background: 'none', border: '1px solid rgba(212,168,67,0.2)', borderRadius: 7, padding: '7px 16px', color: 'var(--gold)', fontSize: 11, fontFamily: "'DM Mono',monospace", letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,168,67,0.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
        >← Back</button>
      </nav>

      <div className="kfx-layout" style={{ maxWidth: 860, margin: '0 auto', padding: '72px 24px 120px', display: 'grid', gridTemplateColumns: '220px 1fr', gap: 60, alignItems: 'start' }}>

        {/* Sidebar TOC */}
        <aside className="kfx-sidebar" style={{ position: 'sticky', top: 80 }}>
          <div className="kfx-sidebar-label" style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 16 }}>On this page</div>
          <nav className="kfx-toc-nav" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {sections.map(s => (
              <a key={s.id} href={`#${s.id}`} style={{ fontSize: 13, color: 'var(--text-45)', textDecoration: 'none', padding: '5px 10px', borderRadius: 6, transition: 'all 0.2s', fontFamily: "'Bricolage Grotesque',sans-serif", lineHeight: 1.4 }}
                onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.background = 'rgba(212,168,67,0.06)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-45)'; e.currentTarget.style.background = 'none'; }}
              >{s.title}</a>
            ))}
          </nav>
          <div className="kfx-sidebar-also" style={{ marginTop: 24, padding: '14px 16px', borderRadius: 8, background: 'rgba(212,168,67,0.04)', border: '1px solid rgba(212,168,67,0.12)' }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.5)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Also read</div>
            <button onClick={() => onNavigate('terms')} style={{ fontSize: 12, color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Bricolage Grotesque',sans-serif", padding: 0, marginBottom: 6, display: 'block' }}>Terms of Service →</button>
            <button onClick={() => onNavigate('privacy')} style={{ fontSize: 12, color: 'var(--gold)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Bricolage Grotesque',sans-serif", padding: 0, display: 'block' }}>Privacy Policy →</button>
          </div>
        </aside>

        {/* Main content */}
        <main>
          {/* Header */}
          <div className="kfx-page-header" style={{ marginBottom: 56 }}>
            <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: 'rgba(212,168,67,0.6)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 12 }}>About</div>
            <h1 style={{ fontSize: 'clamp(28px,5vw,48px)', fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: 16 }}>About KFX Journal</h1>
            <div style={{ marginTop: 20, padding: '14px 16px', borderRadius: 8, background: 'rgba(212,168,67,0.05)', border: '1px solid rgba(212,168,67,0.18)' }}>
              <p style={{ fontSize: 13.5, color: 'rgba(212,168,67,0.8)', lineHeight: 1.7, fontFamily: "'Bricolage Grotesque',sans-serif" }}>
                <strong>KFX Journal</strong> is a forex trading journal and performance analytics platform built for East African traders.
              </p>
            </div>
          </div>

          {/* Sections */}
          {sections.map(section => (
            <PolicySection key={section.id} section={section} />
          ))}
        </main>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid rgba(212,168,67,0.1)', padding: '28px 24px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '6px 10px', fontSize: 12, color: 'var(--text-45)', fontFamily: "'DM Mono',monospace", letterSpacing: '0.04em' }}>
          <span>© {new Date().getFullYear()} KFX Journal</span>
          <span style={{ opacity: 0.35 }}>·</span>
          <button onClick={() => onNavigate('terms')} style={{ color: 'var(--text-45)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontSize: 12, padding: 0, letterSpacing: '0.04em' }}>Terms of Service</button>
          <span style={{ opacity: 0.35 }}>·</span>
          <button onClick={() => onNavigate('privacy')} style={{ color: 'var(--text-45)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Mono',monospace", fontSize: 12, padding: 0, letterSpacing: '0.04em' }}>Privacy Policy</button>
          <span style={{ opacity: 0.35 }}>·</span>
          <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'var(--text-45)', textDecoration: 'none' }}>{SUPPORT_EMAIL}</a>
        </div>
      </div>
    </div>
  );
}
